const GOOGLE_MAPS_API_KEY = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();

export const GOOGLE_MAPS_MAP_ID = (
  process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || ''
).trim();

let googleMapsPromise;
let autocompleteSessionToken;

export const hasGoogleMapsApiKey = () => Boolean(GOOGLE_MAPS_API_KEY);

export const loadGoogleMaps = () => {
  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise((resolve, reject) => {
    const rejectLoad = (error) => {
      googleMapsPromise = undefined;
      reject(error);
    };

    if (!GOOGLE_MAPS_API_KEY) {
      rejectLoad(new Error('Google Maps API key is not configured.'));
      return;
    }
    if (!GOOGLE_MAPS_MAP_ID) {
      rejectLoad(new Error('Google Maps map ID is not configured.'));
      return;
    }

    const loadLibraries = async () => {
      try {
        const [maps, marker, places, geocoding] = await Promise.all([
          window.google.maps.importLibrary('maps'),
          window.google.maps.importLibrary('marker'),
          window.google.maps.importLibrary('places'),
          window.google.maps.importLibrary('geocoding'),
        ]);
        resolve({
          Map: maps.Map,
          AdvancedMarkerElement: marker.AdvancedMarkerElement,
          AutocompleteSessionToken: places.AutocompleteSessionToken,
          AutocompleteSuggestion: places.AutocompleteSuggestion,
          Geocoder: geocoding.Geocoder,
        });
      } catch (error) {
        rejectLoad(error);
      }
    };

    if (window.google?.maps?.importLibrary) {
      loadLibraries();
      return;
    }

    const callbackName = '__galagoGoogleMapsReady';
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}&v=weekly&loading=async&callback=${callbackName}`;
    script.async = true;
    window[callbackName] = () => {
      delete window[callbackName];
      loadLibraries();
    };
    script.onerror = () => {
      delete window[callbackName];
      rejectLoad(new Error('Google Maps JavaScript API script could not be loaded.'));
    };
    document.head.appendChild(script);
  });

  return googleMapsPromise;
};

const getSessionToken = (AutocompleteSessionToken) => {
  if (!autocompleteSessionToken) {
    autocompleteSessionToken = new AutocompleteSessionToken();
  }
  return autocompleteSessionToken;
};

export const searchGoogleLocations = async (query) => {
  const { AutocompleteSessionToken, AutocompleteSuggestion } = await loadGoogleMaps();
  const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
    input: query,
    sessionToken: getSessionToken(AutocompleteSessionToken),
  });

  return (response?.suggestions || [])
    .map((suggestion) => suggestion.placePrediction)
    .filter(Boolean)
    .map((prediction) => ({
      place_id: prediction.placeId,
      name: prediction.mainText?.text || prediction.text?.text || 'Selected location',
      address: prediction.text?.text || prediction.secondaryText?.text || '',
      source: prediction,
    }));
};

export const resolveGoogleLocation = async (prediction) => {
  const place = prediction?.source?.toPlace?.();
  if (!place) return null;

  await place.fetchFields({
    fields: ['displayName', 'formattedAddress', 'location'],
  });
  const lat = place.location?.lat?.();
  const lng = place.location?.lng?.();
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  autocompleteSessionToken = null;
  return {
    name: place.displayName || prediction.name,
    address: place.formattedAddress || prediction.address,
    lat,
    lng,
  };
};

export const reverseGoogleLocation = async (lat, lng) => {
  const { Geocoder } = await loadGoogleMaps();
  const response = await new Geocoder().geocode({ location: { lat, lng } });
  const result = response.results?.[0];
  if (!result) return null;

  return {
    name: result.formatted_address || 'Selected location',
    address: result.formatted_address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
    lat,
    lng,
  };
};
